import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Modal,
  Form,
  Upload,
  message,
  Space,
  Tag,
  Typography,
  InputNumber,
  Switch,
  Divider,
  Collapse,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UploadOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;
const { Panel } = Collapse;

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(
  /\/api$/,
  '',
);

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/storage/')) return `${BACKEND_URL}${url}`;
  return `${BACKEND_URL}/storage/${url}`;
};

const Products = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageFileList, setImageFileList] = useState([]);
  const [productType, setProductType] = useState('product');
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: products } = useQuery(
    ['products', searchTerm, categoryFilter],
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter) params.append('category', categoryFilter);

      const response = await api.get(`/provider/products-services?${params.toString()}`);
      return response.data.data || response.data || [];
    },
    {
      enabled: !!localStorage.getItem('token') && !!localStorage.getItem('user'),
    },
  );

  const { data: categories = [] } = useQuery(
    'categories',
    async () => {
      const response = await api.get('/categories');
      return response.data?.data || response.data || [];
    },
    {
      enabled: !!localStorage.getItem('token') && !!localStorage.getItem('user'),
    },
  );

  const { data: brands = [] } = useQuery(
    'brands',
    async () => {
      const response = await api.get('/brands');
      return response.data?.data || response.data || [];
    },
    {
      enabled: !!localStorage.getItem('token') && !!localStorage.getItem('user'),
    },
  );

  const createProductMutation = useMutation(
    async (productData) => {
      const formData = new FormData();

      Object.keys(productData).forEach((key) => {
        if (key === 'images' && Array.isArray(productData[key])) {
          productData[key].forEach((file, index) => {
            if (file && file.originFileObj) {
              formData.append(`images[${index}]`, file.originFileObj);
            }
          });
        } else if (productData[key] !== null && productData[key] !== undefined) {
          const value = productData[key];
          if (typeof value === 'boolean') {
            formData.append(key, value ? 'true' : 'false');
          } else {
            formData.append(key, value);
          }
        }
      });

      const response = await api.post('/provider/products-services', formData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Product created successfully');
        setIsModalVisible(false);
        setEditingProduct(null);
        setImageFileList([]);
        form.resetFields();
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to create product');
      },
    },
  );

  const updateProductMutation = useMutation(
    async ({ id, ...values }) => {
      const formData = new FormData();

      Object.keys(values).forEach((key) => {
        if (key !== 'images' && values[key] !== undefined && values[key] !== null) {
          // Convert booleans to strings for FormData
          const value = values[key];
          if (typeof value === 'boolean') {
            formData.append(key, value ? 'true' : 'false');
          } else {
            formData.append(key, value);
          }
        }
      });

      if (imageFileList.length > 0) {
        imageFileList.forEach((file, index) => {
          if (file.originFileObj) {
            formData.append(`images[${index}]`, file.originFileObj);
          }
        });
      }

      formData.append('_method', 'PUT');

      const response = await api.post(`/provider/products-services/${id}`, formData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Product updated successfully');
        setIsModalVisible(false);
        setEditingProduct(null);
        setImageFileList([]);
        form.resetFields();
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update product');
      },
    },
  );

  const deleteProductMutation = useMutation(
    (id) => api.delete('/provider/products-services/' + id),
    {
      onSuccess: () => {
        message.success('Product deleted successfully');
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete product');
      },
    },
  );

  const handleSubmit = async (values) => {
    const submitData = { ...values, images: imageFileList };

    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct.id, ...submitData });
    } else {
      createProductMutation.mutate(submitData);
    }
  };

  const handleEdit = useCallback(
    (product) => {
      setEditingProduct(product);
      setProductType(product.type || 'product');

      // Set imageFileList with existing images
      if (product.images && product.images.length > 0) {
        const imageFiles = product.images.map((img) => {
          const url = getImageUrl(img);
          return {
            uid: `-${Date.now()}-${Math.random()}`,
            name: img.image_url?.split('/').pop() || 'image.jpg',
            status: 'done',
            url: url,
          };
        });
        setImageFileList(imageFiles);
      } else {
        setImageFileList([]);
      }

      form.setFieldsValue({
        ...product,
        price: parseFloat(product.price || 0),
        stock_quantity: parseInt(product.stock_quantity || 0),
        has_booking: product.has_booking ?? true,
        booking_duration: product.booking_duration || 60,
        booking_interval: product.booking_interval || 30,
        max_advance_days: product.max_advance_days || 30,
        allow_cancellation: product.allow_cancellation ?? true,
        cancellation_hours: product.cancellation_hours || 24,
        reminder_before: product.reminder_before ?? true,
        reminder_minutes: product.reminder_minutes || 60,
        reminder_repeat: product.reminder_repeat ?? true,
        reminder_interval: product.reminder_interval || 5,
      });
      setIsModalVisible(true);
    },
    [form],
  );

  // Handle edit parameter from URL
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && products && products.length > 0) {
      const productToEdit = products.find((p) => p.id === parseInt(editId));
      if (productToEdit) {
        handleEdit(productToEdit);
        setSearchParams({});
      }
    }
  }, [searchParams, products, setSearchParams, handleEdit]);

  const handleDelete = (productId, e) => {
    if (e) {
      e.stopPropagation();
    }
    Modal.confirm({
      title: 'Delete Product',
      content: 'Are you sure you want to delete this product? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        deleteProductMutation.mutate(productId);
      },
    });
  };

  const getImageUrl = (image) => {
    if (!image) return '';

    let url = '';

    // Check url first (already a full URL from API)
    if (image?.url) {
      url = image.url;

      const match = url.match(/^https?:\/\/localhost\/storage\/(https?:\/\/.+)$/);
      if (match) {
        url = match[1];
      }

      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return getFullUrl(url);
    }

    // Handle image_url property (relative path)
    if (image?.image_url) {
      url = image.image_url;

      const match = url.match(/^https?:\/\/localhost\/storage\/(https?:\/\/.+)$/);
      if (match) {
        url = match[1];
      }

      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return getFullUrl(url);
    }

    if (typeof image === 'string') {
      if (image.startsWith('http://') || image.startsWith('https://')) return image;
      return getFullUrl(image);
    }

    return '';
  };

  return (
    <div>
      <Title level={2} className="page-title">
        Products / Services
      </Title>

      {/* Filters and Actions */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Search
              placeholder="Search products..."
              allowClear
              enterButton={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter by category"
              allowClear
              style={{ width: '100%' }}
              value={categoryFilter}
              onChange={setCategoryFilter}
            >
              {categories?.map((category) => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={10}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsModalVisible(true)}
              >
                Add Product / Service
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Products Grid */}
      <div className="product-grid">
        {products?.map((product) => (
          <Card
            key={product.id}
            hoverable
            className="product-card"
            onClick={() => navigate(`/product/${product.id}`)}
            cover={
              product.images && product.images.length > 0 ? (
                <div
                  style={{
                    position: 'relative',
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fafafa',
                  }}
                >
                  <img
                    alt={product.name}
                    src={getImageUrl(product.images[0])}
                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                    onError={(e) => (e.target.src = '/placeholder.png')}
                  />
                  {product.images.length > 1 && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 12,
                      }}
                    >
                      +{product.images.length}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                  }}
                >
                  No Image
                </div>
              )
            }
            actions={[
              <EditOutlined
                key="edit"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(product);
                }}
              />,
              <DeleteOutlined
                key="delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(product.id, e);
                }}
              />,
            ]}
          >
            <Card.Meta
              title={product.name}
              description={
                <div>
                  <div className="product-price">₨{product.price}</div>
                  <div className="product-description">{product.description}</div>
                  <div style={{ marginTop: 8 }}>
                    {product.category && <Tag color="blue">{product.category.name}</Tag>}
                    {product.type === 'product' && (
                      <>
                        <Tag
                          color={
                            product.stock_quantity > 10
                              ? 'green'
                              : product.stock_quantity > 0
                                ? 'orange'
                                : 'red'
                          }
                        >
                          Stock:{' '}
                          {product.stock_quantity !== undefined && product.stock_quantity !== null
                            ? product.stock_quantity
                            : 0}
                        </Tag>
                        {product.is_available === true ? (
                          <Tag color="green">Available</Tag>
                        ) : (
                          <Tag color="red">Out of Stock</Tag>
                        )}
                      </>
                    )}
                    {product.type === 'service' &&
                      (product.is_available === true ? (
                        <Tag color="green">Available for Booking</Tag>
                      ) : (
                        <Tag color="red">Not Available</Tag>
                      ))}
                  </div>
                </div>
              }
            />
          </Card>
        ))}
      </div>

      {/* Add/Edit Product Modal */}
      <Modal
        title={editingProduct ? 'Edit Product' : 'Add New Product'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingProduct(null);
          setImageFileList([]);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            name: '',
            description: '',
            price: 0,
            stock_quantity: 0,
            type: 'product',
            category_id: null,
            is_available: true,
            has_booking: false,
            allow_cancellation: true,
            reminder_before: true,
            reminder_repeat: false,
          }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="Enter product/service name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true, message: 'Please select type' }]}
              >
                <Select placeholder="Select type" onChange={(value) => setProductType(value)}>
                  <Option value="product">Product</Option>
                  <Option value="service">Service</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category_id"
                label="Category"
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select placeholder="Select category">
                  {categories?.map((category) => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="brand_id" label="Brand">
                <Select placeholder="Select brand" allowClear>
                  {brands?.map((brand) => (
                    <Option key={brand.id} value={brand.id}>
                      {brand.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="price"
                label="Price"
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <Input type="number" placeholder="Enter price" prefix="Rs." />
              </Form.Item>
            </Col>
          </Row>

          {productType === 'product' && (
            <Form.Item name="stock_quantity" label="Stock Quantity">
              <Input type="number" placeholder="Enter stock quantity" />
            </Form.Item>
          )}

          <Form.Item name="is_available" label="Available" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Divider orientation="left">
            <Space>
              <UploadOutlined />
              Images
            </Space>
          </Divider>

          <Form.Item label="Product/Service Images">
            <Upload
              name="images"
              listType="picture"
              maxCount={5}
              multiple
              beforeUpload={() => false}
              accept="image/*"
              fileList={imageFileList}
              onChange={({ fileList }) => setImageFileList(fileList)}
              onRemove={(file) => {
                const newFileList = imageFileList.filter((f) => f.uid !== file.uid);
                setImageFileList(newFileList);
                return true;
              }}
            >
              <Button icon={<UploadOutlined />}>
                {imageFileList.length === 0 ? 'Click to Upload' : 'Add More'}
              </Button>
            </Upload>
          </Form.Item>

          {productType === 'service' && (
            <>
              <Divider orientation="left">
                <Space>
                  <CalendarOutlined />
                  Booking Settings
                </Space>
              </Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="has_booking"
                    label="Allow Online Booking"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Yes" unCheckedChildren="No" defaultChecked />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="booking_duration"
                    label="Service Duration (minutes)"
                    initialValue={60}
                  >
                    <InputNumber min={15} max={480} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="booking_interval"
                    label="Booking Slot Interval (minutes)"
                    initialValue={30}
                  >
                    <InputNumber min={15} max={120} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="max_advance_days"
                    label="Max Advance Booking (days)"
                    initialValue={30}
                  >
                    <InputNumber min={1} max={365} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">
                <Space>
                  <ClockCircleOutlined />
                  Reminder & Cancellation
                </Space>
              </Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="allow_cancellation"
                    label="Allow Cancellation"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Yes" unCheckedChildren="No" defaultChecked />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="cancellation_hours"
                    label="Cancellation Allowed Before (hours)"
                    initialValue={24}
                  >
                    <InputNumber min={1} max={72} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Collapse ghost defaultActiveKey={[]}>
                <Panel header="Notification Settings" key="notifications">
                  <Form.Item
                    name="reminder_before"
                    label="Send Reminder Before Appointment"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Yes" unCheckedChildren="No" defaultChecked />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="reminder_minutes"
                        label="Remind Before (minutes)"
                        initialValue={60}
                      >
                        <Select style={{ width: '100%' }}>
                          <Option value={15}>15 minutes</Option>
                          <Option value={30}>30 minutes</Option>
                          <Option value={60}>1 hour</Option>
                          <Option value={120}>2 hours</Option>
                          <Option value={1440}>1 day</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="reminder_repeat"
                        label="Repeat Reminder"
                        valuePropName="checked"
                      >
                        <Switch checkedChildren="Yes" unCheckedChildren="No" defaultChecked />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="reminder_interval"
                    label="Repeat Every (minutes)"
                    initialValue={5}
                  >
                    <Select style={{ width: '100%' }}>
                      <Option value={5}>Every 5 minutes</Option>
                      <Option value={10}>Every 10 minutes</Option>
                      <Option value={15}>Every 15 minutes</Option>
                      <Option value={30}>Every 30 minutes</Option>
                    </Select>
                  </Form.Item>
                </Panel>
              </Collapse>
            </>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createProductMutation.isLoading || updateProductMutation.isLoading}
              >
                {editingProduct ? 'Update' : 'Create'} Product
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingProduct(null);
                  setImageFileList([]);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Products;
